import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@img/ui';
import { SEO } from '../components/SEO';
import { KEYWORDS_MULLAK } from '../lib/seo-keywords';

/**
 * 🏛️ منصة اتحاد الملاك — Landing Page
 *
 * تستخدم نفس هوية الموقع: خلفية أزرق فاتح، كروت rounded-3xl،
 * إيقاع 3 أقسام (light → dark → light) متناغم مع الصفحة الرئيسية.
 */
export function MullakLandingPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');

  return (
    <div>
      <SEO
        title="منصة اتحاد الملاك الرقمية 🏛️ — العقار بصورة جديدة"
        description="العقار بصورة جديدة — منصة إدارة اتحاد الملاك الإلكترونية الأولى في السعودية. إدارة الجمعيات العمومية، تحصيل الرسوم، فواتير المرافق، الصيانة الدورية، صندوق الاحتياطي، التصويت الإلكتروني، محاضر الاجتماعات، إدارة المرافق المشتركة (مصاعد، حدائق، مسابح، أمن، حراسة). الامتثال الكامل للائحة الهيئة العامة للعقار (REGA) ووزارة الإسكان. شركة صورة العقارية — العقار بصورة جديدة."
        keywords={KEYWORDS_MULLAK}
        canonical="https://img.sa/mullak"
        jsonLd={{
          '@type': 'Service',
          name: 'منصة اتحاد الملاك الرقمية — العقار بصورة جديدة',
          description: 'العقار بصورة جديدة — حل شامل لإدارة اتحادات الملاك والجمعيات السكنية في المملكة العربية السعودية',
          provider: {
            '@type': 'Organization',
            name: 'شركة صورة العقارية',
            slogan: 'العقار بصورة جديدة',
            url: 'https://img.sa',
          },
          serviceType: 'Property Management / Owners Association',
          areaServed: { '@type': 'Country', name: 'Saudi Arabia' },
          url: 'https://img.sa/mullak',
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'خدمات اتحاد الملاك',
            itemListElement: [
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'إدارة الجمعية العمومية' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'تحصيل رسوم اتحاد الملاك' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'إدارة المرافق المشتركة' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'صندوق الاحتياطي والصيانة' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'التصويت الإلكتروني للملاك' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'محاضر الاجتماعات الرقمية' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'إدارة عقود الصيانة الدورية' } },
            ],
          },
        }}
      />
      {/* ━━━━━━━━━━━━━━━━━━━━ HERO — أزرق فاتح بنفس نمط HomePage ━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="relative overflow-hidden text-ink"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 15% 0%, rgba(201,162,79,0.08) 0%, transparent 55%),' +
            'radial-gradient(ellipse 70% 60% at 85% 100%, rgba(107,141,177,0.20) 0%, transparent 55%),' +
            'linear-gradient(135deg, #dde8f5 0%, #e9f0f9 35%, #eef4fb 65%, #dce6f3 100%)',
        }}
      >
        <div className="absolute -right-32 -top-32 w-[480px] h-[480px] rounded-full bg-navy-light/15 blur-3xl animate-pulse-slow" />
        <div className="absolute -left-32 -bottom-32 w-[480px] h-[480px] rounded-full bg-gold/10 blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />

        <div className="max-w-7xl mx-auto px-4 py-14 md:py-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* النص + كود الدخول */}
            <div>
              <span className="inline-flex items-center gap-2 bg-navy-soft text-navy-dark text-xs font-bold px-3 py-1.5 rounded-full mb-4 border border-navy-light/20">
                ⭐ منصة سعودية متكاملة وفق نظام اتحاد الملاك
              </span>
              <h1 className="text-4xl md:text-6xl font-black leading-tight mb-3 text-navy-darker">
                منصة <span className="text-gold-dark">اتحاد الملاك</span> الرقمية
              </h1>
              <p className="text-ink-secondary text-base md:text-lg leading-relaxed mb-6 max-w-xl">
                نظام شامل يُدير جمعيتك من تسجيل REGA إلى المالية والصيانة والتصويت — في منصة واحدة بمعايير عالمية.
              </p>

              {/* صندوق الدخول السريع */}
              <div className="bg-white rounded-2xl p-2 shadow-xl border border-gray-200 mb-5">
                <div className="flex flex-wrap gap-1.5 items-stretch">
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toLowerCase().trim())}
                    onKeyDown={(e) => e.key === 'Enter' && code && navigate(`/associations/${code}`)}
                    placeholder="أدخل كود الجمعية (مثل: tower-a)"
                    dir="ltr"
                    className="flex-1 min-w-[160px] px-4 py-3 text-ink font-bold text-sm bg-transparent border-none outline-none"
                  />
                  <button
                    onClick={() => code && navigate(`/associations/${code}`)}
                    disabled={!code}
                    className="px-6 py-3 rounded-xl bg-gradient-to-l from-gold-dark to-gold hover:from-gold hover:to-gold-light disabled:opacity-50 disabled:cursor-not-allowed text-white font-black text-sm shadow-md transition"
                  >
                    دخول ←
                  </button>
                </div>
              </div>

              <div className="flex gap-3 flex-wrap">
                <Link to="/dashboard/my-associations">
                  <Button variant="outline" size="md">جمعياتي</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="ghost" size="md">طلب عرض سعر</Button>
                </Link>
              </div>

              {/* trust signals */}
              <div className="flex items-center gap-4 flex-wrap mt-6">
                <div className="flex -space-x-2">
                  <span className="w-10 h-10 rounded-full bg-emerald-50 border-2 border-white shadow-sm flex items-center justify-center text-emerald-700 text-lg">📜</span>
                  <span className="w-10 h-10 rounded-full bg-amber-50 border-2 border-white shadow-sm flex items-center justify-center text-amber-700 text-lg">⚖️</span>
                  <span className="w-10 h-10 rounded-full bg-navy-soft border-2 border-white shadow-sm flex items-center justify-center text-navy-dark text-lg">✓</span>
                </div>
                <div>
                  <div className="font-black text-navy-darker">متوافقة 100% مع نظام REGA</div>
                  <div className="text-ink-muted text-sm">نظام ملكية الوحدات العقارية وفرزها وإدارتها</div>
                </div>
              </div>
            </div>

            {/* صورة جانبية + بطاقات عائمة */}
            <div className="relative hidden md:block">
              <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/40 h-[460px] relative">
                <img
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80"
                  alt="Owners Association"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* بطاقة الميزات */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 w-56 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-navy to-navy-dark text-white flex items-center justify-center text-xl">🏛️</div>
                  <div>
                    <div className="text-[10px] text-ink-muted">المنصة الرقمية</div>
                    <div className="font-black text-navy-dark text-sm">إدارة شاملة</div>
                  </div>
                </div>
                <div className="text-[10px] text-emerald-700 font-bold mt-2">✓ 9 وحدات متكاملة</div>
              </div>

              {/* بطاقة الامتثال */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 w-60 animate-float" style={{ animationDelay: '1s' }}>
                <div className="text-[10px] text-ink-muted">الامتثال النظامي</div>
                <div className="font-black text-3xl text-navy-dark">REGA</div>
                <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-l from-gold to-navy w-full" />
                </div>
                <div className="text-[10px] text-navy-darker font-bold mt-1">✓ مهام امتثال جاهزة + تذكيرات</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ FEATURES — كروت ميزات بنفس إيقاع HomePage ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-2">
            الوحدات الرئيسية
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-navy-darker">كل ما تحتاجه جمعيتك</h2>
          <p className="text-ink-secondary mt-2 max-w-2xl mx-auto">
            منصة احترافية مبنية على نظام ملكية الوحدات العقارية وفرزها وإدارتها — تحوّل الإدارة الورقية إلى رقمية بالكامل
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Feature icon="👥" title="إدارة الأعضاء" desc="تسجيل الملاك بحصصهم، الوكلاء، المستأجرين، اللجان مع رابط دعوة لكل عضو + تصدير/استيراد Excel" />
          <Feature icon="💰" title="إدارة مالية شاملة" desc="رسوم دورية تلقائية، فواتير الموردين، مصاريف، احتياطي الصيانة، تقارير ربعية" />
          <Feature icon="🛠️" title="صيانة احترافية" desc="طلبات صيانة بـ 14 فئة، تتبع التقني، سجل زمني، تقييم بعد الإنجاز" />
          <Feature icon="🗳️" title="جمعيات عمومية" desc="جدولة، تصويت إلكتروني، نصاب، محاضر معتمدة قانونياً" />
          <Feature icon="📜" title="امتثال REGA كامل" desc="مهام امتثال جاهزة مع مراجع قانونية وتذكيرات تلقائية" />
          <Feature icon="📰" title="أخبار وإعلانات" desc="إعلانات بأولويات، تثبيت، وصول لكل عضو" />
          <Feature icon="🏦" title="حسابات بنكية" desc="ربط IBAN، تتبع الإيداعات، توليد فواتير سداد" />
          <Feature icon="📁" title="مكتبة وثائق" desc="11 فئة (صكوك، لائحة، عقود، تقارير) مع صلاحيات قراءة" />
          <Feature icon="📱" title="QR codes" desc="رموز للجمعية، الصيانة، الشكاوى — اطبعها والصقها في المداخل" />
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ من نخدم — كرت navy فاتح بنفس نمط Live Auctions ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 pt-6 pb-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl p-8 md:p-10 text-white shadow-lg"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <div className="text-center mb-8">
              <span className="inline-block bg-white/10 backdrop-blur border border-white/15 text-white/90 text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                نخدم جميع الأنواع
              </span>
              <h2 className="text-2xl md:text-3xl font-black mb-2 leading-tight">
                المنصة تتكيّف مع نوع جمعيتك
              </h2>
              <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
                من جمعية مبنى واحد إلى اتحاد جمعيات لمشاريع ضخمة — هيكل مرن يستوعب كل الأحجام
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <TypeCard icon="🏢" title="مبنى واحد" desc="جمعية ملاك مبنى سكني/تجاري" />
              <TypeCard icon="🏘️" title="المجمعات والكومباوند" desc="عدة مبانٍ + مرافق مشتركة" />
              <TypeCard icon="🏙️" title="اتحاد جمعيات" desc="مشاريع كبيرة متعددة المراحل" />
              <TypeCard icon="🚧" title="جمعية مؤقتة" desc="يديرها المطوّر قبل التسليم" />
              <TypeCard icon="🌳" title="جمعية حي" desc="تنموي اجتماعي للحي" />
              <TypeCard icon="🤝" title="Multi-tenant" desc="شركات إدارة عقارية متعددة" />
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ HOW IT WORKS — كرت فاتح بنفس نمط الأدوات الذكية ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl p-8 md:p-10 bg-gradient-to-br from-navy-soft/60 via-white to-gold-soft/40 border border-navy-soft shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <span className="inline-block bg-gold-soft text-gold-dark text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                  كيف نعمل
                </span>
                <h2 className="text-2xl md:text-3xl font-black text-navy-darker mb-2 leading-tight">
                  4 خطوات لإطلاق جمعيتك الرقمية
                </h2>
                <p className="text-ink-secondary text-sm leading-relaxed mb-5 max-w-xl">
                  من التسجيل إلى التشغيل الكامل — فريقنا يصاحبك في كل خطوة
                </p>
                <Link to="/contact">
                  <Button variant="primary" size="lg">ابدأ الآن ←</Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <StepCard num="1" title="التسجيل" desc="إنشاء جمعية + slug خاص" />
                <StepCard num="2" title="استيراد الأعضاء" desc="Excel بالحصص والإيميلات" />
                <StepCard num="3" title="رسوم وحسابات" desc="ربط IBAN + رسوم دورية" />
                <StepCard num="4" title="انطلق" desc="لوحة لكل عضو + QR codes" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ PRICING — الباقات ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block bg-gold-soft text-gold-dark text-xs font-bold px-3 py-1 rounded-full mb-3">
              💎 باقات شفّافة
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-navy-darker mb-3">اختر الباقة المناسبة لجمعيتك</h2>
            <p className="text-ink-secondary max-w-2xl mx-auto">
              ابدأ مجاناً — رقّي لاحقاً مع نمو جمعيتك. كل الباقات تشمل تحديثات مجانية ودعم باللغة العربية.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <PricingCard
              tier="FREE"
              name="مجاني"
              price="0"
              period=""
              tagline="جرّب المنصة بدون أي تكلفة"
              features={[
                'حتى 10 وحدات',
                'إدارة الأعضاء',
                'محاضر اجتماعات',
                'تقارير أساسية',
                'دعم عبر البريد',
              ]}
              cta="ابدأ مجاناً"
              ctaLink="/register"
            />
            <PricingCard
              tier="BASIC"
              name="أساسية"
              price="299"
              period="/شهر"
              tagline="الأنسب للجمعيات الصغيرة"
              features={[
                'حتى 50 وحدة',
                'كل ميزات المجاني',
                'تحصيل الرسوم وفواتير المرافق',
                'إدارة الصيانة والمرافق',
                'تصويت إلكتروني',
                'تقارير مالية متقدمة',
                'دعم عبر البريد + واتساب',
              ]}
              cta="اشترك الآن"
              ctaLink="/contact"
              yearly="2,990 ر.س/سنة (شهرين مجاناً)"
            />
            <PricingCard
              tier="PRO"
              name="احترافية"
              price="999"
              period="/شهر"
              tagline="للجمعيات الكبيرة والمتعدّدة"
              features={[
                'حتى 200 وحدة',
                'كل ميزات الأساسية',
                'هوية مخصّصة (شعار، ألوان، نطاق فرعي)',
                'دعم فني سريع خلال 4 ساعات',
                'تكامل مع ناجز وإيجار',
                'تقارير شاملة قابلة للتصدير',
                'مدير حساب مخصّص',
              ]}
              cta="اشترك الآن"
              ctaLink="/contact"
              yearly="9,990 ر.س/سنة (شهرين مجاناً)"
              highlight
            />
            <PricingCard
              tier="ENTERPRISE"
              name="مؤسسات"
              price="مخصّص"
              period=""
              tagline="للشركات والمطوّرين الكبار"
              features={[
                'عدد غير محدود من الوحدات',
                'كل ميزات الاحترافية',
                'مدير علاقات مخصّص (24/7)',
                'تخصيص كامل + استشارات',
                'تكامل مع أنظمتك الداخلية',
                'تدريب فريقك',
                'SLA رسمي',
              ]}
              cta="تواصل مع المبيعات"
              ctaLink="/contact"
            />
          </div>

          <p className="text-center text-xs text-ink-muted mt-8">
            * تجربة مجانية 14 يوم على كل الباقات المدفوعة. يمكنك إلغاء أو تغيير الباقة في أي وقت.
          </p>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━ CTA — كرت navy فاتح يقفل الإيقاع ━━━━━━━━━━━━━━━━━━━━ */}
      <section className="px-4 pt-6 pb-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative overflow-hidden rounded-3xl p-8 md:p-10 text-white shadow-lg"
            style={{ background: 'var(--gradient-hero)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-black mb-2 leading-tight">
                  جمعيتك تستحق إدارة احترافية
                </h2>
                <p className="text-white/75 text-sm md:text-base">
                  انضم لمنصة شركة صورة العقارية واحصل على نظام إدارة كامل لاتحاد الملاك بمعايير REGA
                </p>
              </div>
              <div className="flex gap-3 flex-wrap md:justify-end">
                <Link to="/contact">
                  <Button variant="gold" size="lg">طلب عرض سعر</Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" size="lg" className="!bg-white !text-navy-dark !border-white">
                    عن المنصة
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* 💎 بطاقة باقة اشتراك */
function PricingCard({
  tier, name, price, period, tagline, features, cta, ctaLink, yearly, highlight,
}: {
  tier: string;
  name: string;
  price: string;
  period: string;
  tagline: string;
  features: string[];
  cta: string;
  ctaLink: string;
  yearly?: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`relative bg-white border-2 ${highlight ? 'border-gold shadow-xl scale-[1.02]' : 'border-gray-200'} rounded-3xl p-6 flex flex-col hover:shadow-lg transition`}
    >
      {highlight && (
        <span className="absolute -top-3 right-1/2 translate-x-1/2 bg-gold text-white text-[10px] font-black px-3 py-1 rounded-full whitespace-nowrap">
          ⭐ الأكثر اختياراً
        </span>
      )}
      <div className="text-center mb-4">
        <div className="text-xs text-ink-muted font-bold mb-1">{tier}</div>
        <h3 className="text-xl font-black text-navy-darker mb-2">{name}</h3>
        <p className="text-xs text-ink-muted">{tagline}</p>
      </div>
      <div className="text-center mb-5 py-3 border-y border-gray-100">
        <div className="flex items-baseline justify-center gap-1">
          <span className="text-3xl font-black text-navy-dark tabular-nums">{price}</span>
          {price !== 'مخصّص' && <span className="text-sm font-bold text-ink-muted">ر.س</span>}
          {period && <span className="text-xs text-ink-muted">{period}</span>}
        </div>
        {yearly && <div className="text-[10px] text-gold-dark mt-1 font-bold">{yearly}</div>}
      </div>
      <ul className="space-y-2 text-sm text-ink-secondary mb-6 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-emerald-500 shrink-0 mt-0.5">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link to={ctaLink}>
        <Button variant={highlight ? 'gold' : 'outline'} size="md" className="w-full">
          {cta}
        </Button>
      </Link>
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━ Sub Components ━━━━━━━━━━━━━━━━━━ */

function Feature({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-6 hover:border-gold/40 hover:shadow-brand transition group">
      <div className="w-12 h-12 rounded-xl bg-navy-soft text-navy-dark flex items-center justify-center text-2xl mb-3 group-hover:scale-110 transition">
        {icon}
      </div>
      <h3 className="font-black text-base text-navy-darker mb-1.5">{title}</h3>
      <p className="text-xs text-ink-muted leading-relaxed">{desc}</p>
    </div>
  );
}

function TypeCard({ icon, title, desc }: { icon: string; title: string; desc: string }) {
  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 rounded-xl p-4 transition">
      <div className="text-2xl mb-1.5">{icon}</div>
      <div className="font-black text-sm">{title}</div>
      <div className="text-[11px] text-white/60 mt-0.5">{desc}</div>
    </div>
  );
}

function StepCard({ num, title, desc }: { num: string; title: string; desc: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 hover:border-gold transition">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-6 h-6 rounded-full bg-navy-dark text-white text-xs font-black flex items-center justify-center">
          {num}
        </span>
        <div className="font-black text-sm text-navy-darker">{title}</div>
      </div>
      <div className="text-[11px] text-ink-muted">{desc}</div>
    </div>
  );
}
